import React, { Suspense } from 'react';
import { Routes, Route, useParams, useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from './PageLayout';
import {
  mainRoutes,
  utilityRoutes,
  staticRoutes,
  generateAllLanguageRoutes,
} from './routeConfig';
import { useLanguage } from '../hooks/useLanguage';
import { HomePage } from '../pages/HomePage';
import type { Product, WeightVariant } from '../components/ProductCard';

// Loading component for Suspense
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-amber-800 font-medium">Loading...</p>
      </div>
    </div>
  );
}

// Language Route Wrapper - detects language from URL and syncs with i18n
function LanguageRouteWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { changeLanguage, isLanguageActive, currentLanguage } = useLanguage();
  const [isLanguageSynced, setIsLanguageSynced] = React.useState(false);

  // Sync URL language with i18n
  React.useEffect(() => {
    // Extract language from pathname (e.g., "/de" -> "de", "/pl/catalog" -> "pl")
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const langFromPath = pathSegments[0];

    let targetLanguage = 'en';

    if (langFromPath && isLanguageActive(langFromPath)) {
      targetLanguage = langFromPath;
    }

    // Only change if different from current - no delays needed
    if (currentLanguage !== targetLanguage) {
      changeLanguage(targetLanguage as any, true); // Skip navigation since we're syncing from URL
    }

    // Mark as synced immediately - no loading state needed
    setIsLanguageSynced(true);
  }, [location.pathname, changeLanguage, isLanguageActive, currentLanguage]);

  // Show loading until language is synced
  if (!isLanguageSynced) {
    return <PageLoader />;
  }

  return <>{children}</>;
}

interface AppRouterProps {
  // Cart state
  onCartClick: () => void;
  // Navigation handlers
  onCatalogClick: () => void;
  onRecipesClick: () => void;
  onMenuClick: () => void;
  onCategoryClick: (category: string, subcategory?: string) => void;
  onAccountSettingsClick: () => void;
  onPageNavigation: (page: string) => void;
  onHomeNavigation: () => void;
  // Product/Recipe handlers
  onProductClick: (product: Product) => void;
  onRecipeClick: (recipe?: any) => void;
  onAddToCart: (product: Product, variant?: WeightVariant) => void;
  // Catalog filter for CatalogPage
  catalogFilter?: {
    category?: string;
    subcategory?: string;
    categories?: string[];
  };
}

export function AppRouter({
  onCartClick,
  onCatalogClick,
  onRecipesClick,
  onMenuClick,
  onCategoryClick,
  onAccountSettingsClick,
  onPageNavigation,
  onHomeNavigation,
  onProductClick,
  onRecipeClick,
  onAddToCart,
  catalogFilter,
}: AppRouterProps) {
  const navigate = useNavigate();
  // Create reusable layout props
  const layoutProps = {
    onCartClick,
    onCatalogClick,
    onRecipesClick,
    onMenuClick,
    onCategoryClick,
    onAccountSettingsClick,
    onProductClick,
    onRecipeClick,
    onAddToCart,
    onPageNavigation,
  };

  // Helper component to pass URL params to route components
  function RouteComponentWrapper({
    route,
    additionalProps = {},
  }: {
    route: any;
    additionalProps?: any;
  }) {
    const params = useParams();
    const Component = route.component;

    return (
      <PageLayout
        showHeader={route.requiresHeader}
        showFooter={route.requiresFooter}
        seoTitle={route.seoTitle}
        seoDescription={route.seoDescription}
        {...layoutProps}
      >
        <Component
          {...additionalProps}
          {...params} // Pass URL parameters (handle, slug, etc.)
          // Pass through all handlers
          onAddToCart={onAddToCart}
          onProductClick={onProductClick}
          onRecipeClick={onRecipeClick}
          onCatalogClick={onCatalogClick}
          onRecipesClick={onRecipesClick}
          onMenuClick={onMenuClick}
          onBack={() => navigate(-1)}
          onCartClick={onCartClick}
          onPageNavigation={onPageNavigation}
          onHomeNavigation={onHomeNavigation}
          onCategoryClick={onCategoryClick}
          onAccountSettingsClick={onAccountSettingsClick}
          // Page-specific props - initialFilter removed, using zustand now
        />
      </PageLayout>
    );
  }

  // Helper function to create route component with layout
  const createRouteElement = (route: any, additionalProps: any = {}) => {
    return (
      <RouteComponentWrapper route={route} additionalProps={additionalProps} />
    );
  };

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* English routes (no language prefix) */}
        {mainRoutes.map(route => (
          <Route
            key={`en-${route.path}`}
            path={route.path === '' ? '/' : `/${route.path}`}
            element={createRouteElement(route)}
          />
        ))}

        {/* Explicit language routes for homepage */}
        <Route
          path="/de"
          element={
            <LanguageRouteWrapper>
              <PageLayout
                showHeader={true}
                showFooter={true}
                seoTitle="homepage.meta.title"
                seoDescription="homepage.meta.description"
                {...layoutProps}
              >
                <HomePage
                  onAddToCart={onAddToCart}
                  onProductClick={onProductClick}
                  onCatalogClick={onCatalogClick}
                  onRecipeClick={onRecipeClick}
                  onRecipesClick={onRecipesClick}
                />
              </PageLayout>
            </LanguageRouteWrapper>
          }
        />
        <Route
          path="/pl"
          element={
            <LanguageRouteWrapper>
              <PageLayout
                showHeader={true}
                showFooter={true}
                seoTitle="homepage.meta.title"
                seoDescription="homepage.meta.description"
                {...layoutProps}
              >
                <HomePage
                  onAddToCart={onAddToCart}
                  onProductClick={onProductClick}
                  onCatalogClick={onCatalogClick}
                  onRecipeClick={onRecipeClick}
                  onRecipesClick={onRecipesClick}
                />
              </PageLayout>
            </LanguageRouteWrapper>
          }
        />
        <Route
          path="/uk"
          element={
            <LanguageRouteWrapper>
              <PageLayout
                showHeader={true}
                showFooter={true}
                seoTitle="homepage.meta.title"
                seoDescription="homepage.meta.description"
                {...layoutProps}
              >
                <HomePage
                  onAddToCart={onAddToCart}
                  onProductClick={onProductClick}
                  onCatalogClick={onCatalogClick}
                  onRecipeClick={onRecipeClick}
                  onRecipesClick={onRecipesClick}
                />
              </PageLayout>
            </LanguageRouteWrapper>
          }
        />

        {/* Explicit language routes for other pages */}
        {mainRoutes
          .filter(route => route.path !== '')
          .map(route => [
            // German routes
            <Route
              key={`de-${route.path}`}
              path={`/de/${route.path}`}
              element={
                <LanguageRouteWrapper>
                  {createRouteElement(route)}
                </LanguageRouteWrapper>
              }
            />,
            // Polish routes
            <Route
              key={`pl-${route.path}`}
              path={`/pl/${route.path}`}
              element={
                <LanguageRouteWrapper>
                  {createRouteElement(route)}
                </LanguageRouteWrapper>
              }
            />,
            // Ukrainian routes
            <Route
              key={`uk-${route.path}`}
              path={`/uk/${route.path}`}
              element={
                <LanguageRouteWrapper>
                  {createRouteElement(route)}
                </LanguageRouteWrapper>
              }
            />,
          ])
          .flat()}

        {/* Utility routes (single language) */}
        {utilityRoutes.map(route => (
          <Route
            key={`util-${route.path}`}
            path={`/${route.path}`}
            element={createRouteElement(route)}
          />
        ))}

        {/* Static/legal routes (single language) */}
        {staticRoutes.map(route => (
          <Route
            key={`static-${route.path}`}
            path={`/${route.path}`}
            element={createRouteElement(route)}
          />
        ))}
      </Routes>
    </Suspense>
  );
}
